// Signing in with desyr ID (36-desyr-id.md, Behaviour 1 and 4): OpenID Connect's authorization
// code flow as a public client with PKCE (S256), its ID token checked here (issuer, audience, nonce,
// expiry and its signature against the service's keys), the tokens renewed one at a time, and the
// service's end-session address for signing out. Every page of desyr ID opens in this window.
import type { DesyrIdConfig } from '../core/config';
import { atMost, leaveFor } from '../core/device';
import { random, sha256url } from '../core/mail';
import { currentLanguage } from '../i18n';
import { lastEmail, rememberEmail } from '../signin/company';
import { authGen, dropAuto, keepId, keepPending, markSignedOut, readId, signedOutMark, takePending, type DesyrId } from './store';

export class IdError extends Error {
  /** `page`: the page a cancelled or failed sign-in was for (04 step 3: the next sign-in still opens it). */
  constructor(
    public readonly reason: 'failed' | 'unavailable' | 'cancelled',
    public readonly page?: string,
  ) {
    super(reason);
  }
}

interface Discovery {
  issuer: string;
  authorization_endpoint: string;
  token_endpoint: string;
  jwks_uri: string;
  end_session_endpoint?: string;
}
let discovery: Promise<Discovery> | undefined;
/** The service's discovery document (the issuer followed by `.well-known/openid-configuration`), kept for the tab. */
function discover(config: DesyrIdConfig): Promise<Discovery> {
  discovery ??= fetch(`${config.issuer.replace(/\/+$/, '')}/.well-known/openid-configuration`, { credentials: 'omit' })
    .then((r) => (r.ok ? r.json() : Promise.reject(new IdError('unavailable'))))
    .catch((e) => ((discovery = undefined), Promise.reject(e instanceof IdError ? e : new IdError('unavailable'))));
  return discovery;
}

export const doneAddress = () => `${location.origin}/id/done/`;

/**
 * Whether desyr ID answers now (36, Behaviour 4.1 and 4.3.2, v1.58.74), asked before the window goes to an
 * end-session address so it never ends on the browser's error page: offline no, else its discovery
 * document fetched afresh, answered by `by` (ms since the epoch).
 */
export async function idAnswers(config: DesyrIdConfig, by: number): Promise<boolean> {
  if (navigator.onLine === false) return false;
  const wait = Math.max(0, by - Date.now());
  const ask = fetch(`${config.issuer.replace(/\/+$/, '')}/.well-known/openid-configuration`, { credentials: 'omit', cache: 'no-store', signal: AbortSignal.timeout(wait || 1) })
    .then((r) => r.ok)
    .catch(() => false);
  return (await atMost(ask, wait)) ?? false;
}

/**
 * "Continue with desyr ID": to the service's sign-in in this window, coming back to `page`; the last
 * desyr ID email on this device filled in (v1.58.15). `again`: "Sign in again" for a passkey (prompt=login),
 * as every sign-in while this device is marked signed out (v1.58.63, Behaviour 4.3). `deadline`: the
 * automatic jump's 10 s (Starting signed out 2e); once it has passed, nothing jumps any more.
 */
export async function startSignIn(config: DesyrIdConfig, page: string, prompt?: { passkey?: boolean; again?: boolean }, deadline?: AbortSignal): Promise<void> {
  const d = await discover(config);
  deadline?.throwIfAborted();
  const [verifier, state, nonce] = [random(32), random(16), random(16)];
  const challenge = await sha256url(verifier);
  if (!keepPending(state, { verifier, nonce, page, gen: authGen() })) throw new IdError('failed');
  deadline?.throwIfAborted();
  const url = new URL(d.authorization_endpoint);
  url.search = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: doneAddress(),
    scope: 'openid profile email',
    state,
    nonce,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    ui_locales: currentLanguage(),
    ...(lastEmail() ? { login_hint: lastEmail() } : {}),
    // A fresh sign-in (37, Behaviour 3, v1.58.2): prompt=login and max_age=0, no acr_values.
    ...(prompt?.passkey ? { prompt: 'login', max_age: '0' } : prompt?.again || signedOutMark() ? { prompt: 'login' } : {}),
  }).toString();
  location.assign(url.href);
}

/** "Sign in again" for a circle that needs a passkey sign-in (Behaviour 6.3): back to this page, then the call again. */
/**
 * After the window was sent to desyr ID: when it is still here `ms` later (WebKit shows no error page for a
 * refused address and simply stays), `failed` runs, so the card shows instead of a screen with no way on
 * (36, Starting signed out 2e: a failure shows the card). Once the page is going, nothing runs.
 */
export function unlessLeft(failed: () => void, ms = 10_000): void {
  const stay = setTimeout(failed, ms);
  addEventListener('pagehide', () => clearTimeout(stay), { once: true });
}

export const signInAgain = (config: DesyrIdConfig) => (dropAuto(), startSignIn(config, location.pathname + location.search, { again: true }));

const decode = (part: string) => JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(part.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0))));
const bytes = (part: string) => Uint8Array.from(atob(part.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0));

/** The algorithms a desyr ID token may be signed with, as WebCrypto names them. */
const ALGORITHMS: Record<string, { import: RsaHashedImportParams | EcKeyImportParams; verify: AlgorithmIdentifier | EcdsaParams }> = {
  RS256: { import: { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' }, verify: { name: 'RSASSA-PKCS1-v1_5' } },
  ES256: { import: { name: 'ECDSA', namedCurve: 'P-256' }, verify: { name: 'ECDSA', hash: 'SHA-256' } },
};

/** An ID token's claims once its signature, issuer, audience, nonce (when given) and expiry hold. */
export async function checkIdToken(token: string, config: DesyrIdConfig, jwks: { keys: (JsonWebKey & { kid?: string })[] }, nonce?: string, now = Date.now()): Promise<Record<string, unknown>> {
  const [head, body, signature] = token.split('.');
  const header = decode(head) as { alg: string; kid?: string };
  const claims = decode(body) as Record<string, unknown>;
  const algorithm = ALGORITHMS[header.alg];
  const jwk = jwks.keys.find((k) => !header.kid || k.kid === header.kid);
  if (!algorithm || !jwk) throw new IdError('failed');
  const key = await crypto.subtle.importKey('jwk', jwk, algorithm.import, false, ['verify']);
  const signed = await crypto.subtle.verify(algorithm.verify, key, bytes(signature), new TextEncoder().encode(`${head}.${body}`));
  const audience = Array.isArray(claims.aud) ? claims.aud : [claims.aud];
  if (!signed || claims.iss !== config.issuer || !audience.includes(config.clientId)) throw new IdError('failed');
  if ((nonce !== undefined && claims.nonce !== nonce) || typeof claims.exp !== 'number' || claims.exp * 1000 < now - 60_000) throw new IdError('failed');
  return claims;
}

/**
 * Whether the sign-in used a passkey, and when it was (36, Behaviour 1.3, v1.58.11): taken from the
 * sign-in's first ID token. A renewal of the same sign-in (the same `auth_time`) keeps them, although
 * its `amr` may no longer say "mfa".
 */
export function signInOf(claims: Record<string, unknown>, before: DesyrId | undefined, renewal: boolean): Pick<DesyrId, 'passkey' | 'authTime'> {
  const authTime = typeof claims.auth_time === 'number' ? claims.auth_time : undefined;
  if (renewal && before && (authTime === undefined || authTime === before.authTime)) return { passkey: before.passkey, authTime: before.authTime };
  return { passkey: Array.isArray(claims.amr) && claims.amr.includes('mfa'), authTime: authTime ?? before?.authTime };
}

/** The tokens of a code or refresh answer, checked and kept as this device's desyr ID. */
async function keepTokens(config: DesyrIdConfig, answer: Record<string, unknown>, gen: number, nonce?: string): Promise<DesyrId> {
  const d = await discover(config);
  if (typeof answer.id_token !== 'string') throw new IdError('failed');
  const jwks = await fetch(d.jwks_uri, { credentials: 'omit' }).then((r) => r.json(), () => Promise.reject(new IdError('unavailable')));
  const claims = await checkIdToken(answer.id_token, config, jwks, nonce);
  const before = readId();
  if (before && before.sub !== claims.sub && nonce === undefined) throw new IdError('failed');
  const id: DesyrId = {
    issuer: config.issuer,
    sub: String(claims.sub),
    email: String(claims.email ?? before?.email ?? ''),
    name: typeof claims.name === 'string' ? claims.name : before?.name,
    ...signInOf(claims, before, nonce === undefined),
    refreshToken: typeof answer.refresh_token === 'string' ? answer.refresh_token : before?.refreshToken,
    idToken: answer.id_token,
    idUntil: Number(claims.exp) * 1000,
    accessToken: typeof answer.access_token === 'string' ? answer.access_token : undefined,
    accessUntil: typeof answer.expires_in === 'number' ? Date.now() + answer.expires_in * 1000 : undefined,
  };
  // Signed out meanwhile (in any tab): the late answer stores nothing (Behaviour 4.4.3, v1.58.63).
  if (gen !== authGen()) throw new IdError('cancelled');
  keepId(id);
  return id;
}

async function token(config: DesyrIdConfig, fields: Record<string, string>): Promise<Record<string, unknown>> {
  const d = await discover(config);
  const answer = await fetch(d.token_endpoint, {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ client_id: config.clientId, ...fields }),
  }).catch(() => undefined);
  if (!answer) throw new IdError('unavailable');
  const body = await answer.json().catch(() => ({}));
  if (!answer.ok) throw Object.assign(new IdError(answer.status >= 500 ? 'unavailable' : 'failed'), { status: answer.status });
  return body;
}

/**
 * Back at `/id/done/` (Behaviour 1.2): the state matched to its sign-in, the code exchanged, the
 * tokens checked and kept; the page to go on to. The person's "back" on the desyr ID page: cancelled.
 */
export async function finishSignIn(config: DesyrIdConfig, url: URL): Promise<string> {
  const pending = takePending(url.searchParams.get('state') ?? '');
  // No entry while signed out here (signed out meanwhile, 4.4): the card, no error, nothing exchanged (v1.58.63).
  if (!pending && signedOutMark()) throw new IdError('cancelled');
  if (url.searchParams.get('error')) throw new IdError(pending ? 'cancelled' : 'failed', pending?.page);
  const code = url.searchParams.get('code');
  if (!pending || !code) throw new IdError('failed');
  const answer = await token(config, { grant_type: 'authorization_code', code, redirect_uri: doneAddress(), code_verifier: pending.verifier });
  rememberEmail((await keepTokens(config, answer, pending.gen ?? 0, pending.nonce)).email); // the next sign-in's login_hint, kept across sign-out
  return pending.page;
}

/** Less than a minute left on the ID token. */
const short = (id: DesyrId) => id.idUntil - Date.now() <= 60_000;

let renewing: Promise<DesyrId | undefined> | undefined;
/**
 * Renewed with the refresh token, one renewal at a time across all tabs (the Web Lock `desyr_id_renew`,
 * v1.58.63): a tab that waited reads `desyr_id` again and takes what another tab renewed meanwhile.
 * Undefined when the desyr ID session is over, or when a sign-out came in between (nothing stored).
 */
export function renew(config: DesyrIdConfig): Promise<DesyrId | undefined> {
  const before = readId();
  const gen = authGen();
  const run = async () => {
    const now = readId();
    if (now && now.idToken !== before?.idToken && !short(now)) return now;
    const refresh = now?.refreshToken;
    if (!refresh) return undefined;
    try {
      const id = await keepTokens(config, await token(config, { grant_type: 'refresh_token', refresh_token: refresh }), gen);
      dispatchEvent(new Event('desyr:id')); // the session changed: a picture that didn't load is tried again (38)
      return id;
    } catch (e) {
      if (e instanceof IdError && e.reason === 'unavailable') throw e;
      return undefined; // the refresh token refused (or a sign-out came in between): the next action signs in again
    }
  };
  renewing ??= (navigator.locks ? navigator.locks.request('desyr_id_renew', run) : run()).finally(() => (renewing = undefined));
  return renewing;
}

/** The ID token, renewed when it has less than a minute left (`force`: refused by a service). */
export async function idToken(config: DesyrIdConfig, force = false): Promise<string | undefined> {
  const id = readId();
  if (!id) return undefined;
  if (!force && id.idToken && id.idUntil - Date.now() > 60_000) return id.idToken;
  return (await renew(config))?.idToken;
}

/**
 * The end-session address (RP-Initiated Logout), back to `/login` with `state` (v1.58.63: the return
 * with it confirms the end, Behaviour 4.3); undefined when the service has none. Without `id`: Finish
 * signing out, with `client_id` only (the ID token is never kept after sign-out).
 */
export async function endSessionAddress(config: DesyrIdConfig, id: DesyrId | undefined, state: string): Promise<string | undefined> {
  const d = await discover(config).catch(() => undefined);
  if (!d?.end_session_endpoint) return undefined;
  const url = new URL(d.end_session_endpoint);
  url.search = new URLSearchParams({ ...(id ? { id_token_hint: id.idToken } : {}), post_logout_redirect_uri: `${location.origin}/login`, client_id: config.clientId, state }).toString();
  return url.href;
}

/** "Finish signing out" (Behaviour 4.3.2): the end-session address once more, with a new `state`, in this window. */
export async function finishSigningOut(config: DesyrIdConfig): Promise<void> {
  const by = Date.now() + 10_000;
  const state = random(16);
  // Without desyr ID's answer within 10 s (or offline) the card stays, with the line and Finish (4.3.2, v1.58.74).
  const [address, answers] = await Promise.all([atMost(endSessionAddress(config, undefined, state).catch(() => undefined), 10_000), idAnswers(config, by)]);
  if (!address || !answers) throw new IdError('unavailable');
  markSignedOut(state);
  leaveFor(address, true);
}
